import _ from "lodash";
import { OrderedLineItem, ICart } from "constants/cart";
import { isDeliveryWindowSuitable } from "./delivery-windows/compare-dates";
import { IDeliveryWindowEntities, DeliveryWindow } from "records/item/delivery-window";
import moment, { Moment } from "moment";
import { formatMessage } from "lib/globalize-util";
import { IDeliveryWindowType } from "constants/delivery-window-types";
import { List, Map, fromJS } from "immutable";

export const NONE_WAREHOUSE = "none";

export interface AvailabilityEntities {
    [id: string]: Map<"delivery_windows", List<string>>;
}

interface Quantities {
    [sizeIdToDeliveryWindowId: string]: number;
}

export function getDefaultWarehouse(options: {
    cart: ICart;
    productId: string;
    warehouses: string[];
    warehouse?: string;
}) {
    const { cart, productId, warehouses, warehouse } = options;
    const ordered = new Set(getOrderedWarehouses(cart, productId));
    return [
        warehouse, // usually a url query param
        warehouses?.find((w) => ordered.has(w)), // first ordered in the product warehouses list
        _.first(warehouses), // any first
    ].find((w) => warehouses?.includes(w));
}

/** Navigates and focuses a user on a specific line item in the /cart */
export function goToCartLineItem(lineItemId: string, { router, scope }) {
    if (lineItemId) {
        router.push({ pathname: `/brand/${scope.brand._id}/cart`, state: { toggleCart: { lineItemId } } });
    }
}

export const getAttributes = (
    orderedLineItems: OrderedLineItem[],
    attributeMetadata: Map<string, any>[]
): Map<string, any> | null => {
    const attributes = getLineItemAttributes(_.first(orderedLineItems), attributeMetadata);
    const areAllSame = _.every(orderedLineItems, (li) =>
        _.isEqual(getLineItemAttributes(li, attributeMetadata), attributes)
    );
    return areAllSame ? fromJS(attributes) : null;
};

export const getLineItemAttributes = (li: OrderedLineItem, attributesMetadata: Map<string, any>[]) =>
    _.reduce(
        attributesMetadata,
        (acc, attribute) => {
            const key = attribute.get("key");
            acc[key] = !attribute.get("values") && attribute.get("multi") ? li?.[key]?.join(", ") : li?.[key];
            return acc;
        },
        {}
    );

export const patchDeliveryWindowsAndAvailability = (
    orderedLineItems: OrderedLineItem[],
    deliveryWindowEntities: IDeliveryWindowEntities,
    availabilityEntities: AvailabilityEntities = {},
    productId: string,
    warehouse: string = NONE_WAREHOUSE
): [IDeliveryWindowEntities, AvailabilityEntities] => {
    const availabilityKey = `${productId}_${warehouse}`;
    const deliveryWindowIds: string[] = availabilityEntities[availabilityKey]?.get("delivery_windows")?.toJS() ?? [];

    let dwEntities = deliveryWindowEntities || {};
    const ids = new Set<string>(deliveryWindowIds);
    const newIds = new Set<string>();

    _.each(orderedLineItems, (li) => {
        const [newId, originalId, dw] = getLineItemDeliveryWindow(li, dwEntities, deliveryWindowIds, availabilityKey);

        newIds.add(newId);
        ids.delete(originalId);

        dwEntities = { ...dwEntities, [newId]: dw };
    });

    // Merge new and existing delivery window IDs
    const usedIds = _.sortBy(_.uniq([...newIds, ...ids]), [getDate(dwEntities, "start"), getDate(dwEntities, "end")]);
    const availEntities = availabilityEntities[availabilityKey]?.set("delivery_windows", fromJS(usedIds));

    // show only modified or untouched predefined DWs
    return [_.pick(dwEntities, usedIds), _.pickBy({ ...availabilityEntities, [availabilityKey]: availEntities })];
};

/**
 * In the case of empty/"none" warehouse,
 * we don't have warehouse selection on the PDP => get all the line items
 */
export const getOrderedLineItems = (cart: ICart, productId: string, warehouse: string = NONE_WAREHOUSE) => {
    const orderedProduct = cart?.orderedQuantities?.[productId];
    const lineItems = warehouse === NONE_WAREHOUSE ? _.values(orderedProduct).flat() : orderedProduct?.[warehouse];
    return lineItems ?? [];
};

export const getQuantities = (
    lineItems: OrderedLineItem[],
    deliveryWindowEntities: IDeliveryWindowEntities,
    { shortId }: { shortId?: boolean }
): Quantities => {
    const get = getLineItemQuantities(deliveryWindowEntities, shortId);
    return _.reduce(lineItems, (quantities, li) => get(quantities, li), {} as Quantities);
};

export const getNotes = (orderedLineItems: OrderedLineItem[]): string => {
    const notes = _.first(orderedLineItems)?.notes || "";
    const areAllSame = _.every(orderedLineItems, (li) => (li.notes || "") === notes);
    return areAllSame ? notes : null;
};

const getLineItemQuantities = (deliveryWindowEntities: IDeliveryWindowEntities, shortId: boolean) => {
    // at the moment all DWs with quantities have lineItemId
    const dwByLineItemId = _.keyBy(_.values(deliveryWindowEntities), (dw) => dw.get("lineItemId")) as Record<
        string,
        DeliveryWindow
    >;
    return (quantities: Quantities, li: OrderedLineItem) => {
        const dwId = dwByLineItemId[li._id]?.get("_id");
        _.each(li.quantities, (quantity, sizeName) => {
            const key = shortId ? sizeName : `${sizeName}::${dwId}`;
            quantities[key] = (quantities[key] || 0) + (quantity || 0);
        });
        return quantities;
    };
};

const getDate = (entities: IDeliveryWindowEntities, field: "start" | "end") => (id: string) =>
    entities[id]?.get(field)?.valueOf();
const getProductOrderedQuantities = (cart: ICart, productId: string) => cart?.orderedQuantities?.[productId];
const getOrderedWarehouses = (cart: ICart, productId: string) =>
    _.without(_.keys(getProductOrderedQuantities(cart, productId)), NONE_WAREHOUSE);

const findDeliveryWindow = (
    entities: IDeliveryWindowEntities,
    ids: string[],
    options: { start: Moment; end: Moment; prebook: boolean }
): [string | null, DeliveryWindow | null] => {
    const existing = _.chain(entities)
        .entries()
        .sortBy(([, dw]) => dw.get("adjustable_dates")) // consider **not** adjustable first (exact match)
        .find(([id, dw]) => ids.includes(id) && isDeliveryWindowSuitable(dw?.toJS(), options))
        .value() as [string, DeliveryWindow] | undefined;
    return existing ?? [null, null];
};

const getLineItemDeliveryWindow = (
    li: OrderedLineItem,
    entities: IDeliveryWindowEntities,
    ids: string[],
    idPrefix: string
) => {
    const [start, end] = [moment.utc(li.ship_start), moment.utc(li.ship_end)];
    const newId = `${idPrefix}_${li._id}`;

    const [originalId, existing] = findDeliveryWindow(entities, ids, { start, end, prebook: li.prebook });
    const displayName =
        !existing || existing.get("display_name") === formatMessage("Choose Date Range")
            ? `${start.format("MMM D YYYY")} - ${end.format("MMM D YYYY")}` // rename with actual line item dates
            : existing.get("display_name");

    const dw = {
        adjustable_dates: true,
        type: (li.prebook
            ? "prebook"
            : start.isAfter(moment.utc(), "day")
              ? "future"
              : "immediate") as IDeliveryWindowType,
        prebook: !!li.prebook,
        ...(existing?.toJS() || {}),
        start,
        end,
        _id: newId,
        lineItemId: li._id,
        display_name: displayName,
    };

    return [newId, originalId, new DeliveryWindow(dw)] as const;
};
